import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { 
  Upload, 
  File as FileIcon, 
  Download, 
  Trash2, 
  Edit, 
  Folder,
  Tag,
  HardDrive,
  FileText,
  Image,
  Video,
  Music,
  Archive,
  X,
  Plus
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

export default function UserStorageLibrary({ onClose }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showEditFile, setShowEditFile] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [uploadData, setUploadData] = useState({
    file: null,
    description: "",
    tags: "",
    folder: ""
  });
  const [filter, setFilter] = useState({
    folder: "",
    tags: ""
  });
  const [stats, setStats] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filter.folder) params.append('folder', filter.folder);
      if (filter.tags) params.append('tags', filter.tags);
      
      const response = await axios.get(`${API}/user-files?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFiles(response.data);
    } catch (error) {
      toast.error("Failed to load files");
      console.error("Error fetching files:", error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user-files/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
    fetchStats();
  }, [fetchFiles, fetchStats]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setUploadData(prev => ({ ...prev, file }));
      setShowUpload(true);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadData.file) {
      toast.error("Please select a file");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('file', uploadData.file);
      if (uploadData.description) formData.append('description', uploadData.description);
      if (uploadData.tags) formData.append('tags', uploadData.tags);
      if (uploadData.folder) formData.append('folder', uploadData.folder);

      await axios.post(`${API}/user-files/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success("File uploaded successfully!");
      setShowUpload(false);
      setUploadData({ file: null, description: "", tags: "", folder: "" });
      fetchFiles();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setLoading(false);
    }
  };

  const handleFileDownload = async (fileId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user-files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("File downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download file");
    }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/user-files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("File deleted successfully!");
      fetchFiles();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const handleFileEdit = (file) => {
    setEditingFile(file);
    setShowEditFile(true);
  };

  const handleFileUpdate = async () => {
    if (!editingFile) return;

    try {
      const token = localStorage.getItem('token');
      
      const updateData = {
        description: editingFile.description || null,
        tags: editingFile.tags || [],
        folder: editingFile.folder || null
      };

      await axios.put(`${API}/user-files/${editingFile.id}`, updateData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success("File updated successfully!");
      setShowEditFile(false);
      setEditingFile(null);
      fetchFiles();
    } catch (error) {
      toast.error("Failed to update file");
      console.error("Update error:", error);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return <Image className="w-8 h-8 text-blue-500" />;
    if (fileType.startsWith('video/')) return <Video className="w-8 h-8 text-purple-500" />;
    if (fileType.startsWith('audio/')) return <Music className="w-8 h-8 text-green-500" />;
    if (fileType.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (fileType.includes('zip') || fileType.includes('rar')) return <Archive className="w-8 h-8 text-yellow-500" />;
    return <FileIcon className="w-8 h-8 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uniqueFolders = [...new Set(files.filter(f => f.folder).map(f => f.folder))];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-emerald-900 flex items-center gap-3">
            <HardDrive className="w-8 h-8 text-emerald-600" />
            User Storage Library
          </DialogTitle>
          <DialogDescription>
            Manage your personal files and documents
          </DialogDescription>
        </DialogHeader>

        {/* Storage Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-gradient-to-r from-emerald-50 to-teal-50">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-emerald-600">{stats.total_files}</div>
                <div className="text-sm text-gray-600">Total Files</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-blue-600">{stats.total_size_mb} MB</div>
                <div className="text-sm text-gray-600">Storage Used</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-purple-600">{stats.file_types?.length || 0}</div>
                <div className="text-sm text-gray-600">File Types</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Actions and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Button
            onClick={() => setShowUpload(true)}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload File
          </Button>
          
          <div className="flex gap-2 flex-1">
            <select
              value={filter.folder}
              onChange={(e) => setFilter(prev => ({ ...prev, folder: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">All Folders</option>
              {uniqueFolders.map(folder => (
                <option key={folder} value={folder}>{folder}</option>
              ))}
            </select>
            
            <Input
              placeholder="Filter by tags..."
              value={filter.tags}
              onChange={(e) => setFilter(prev => ({ ...prev, tags: e.target.value }))}
              className="flex-1"
            />
          </div>
        </div>

        {/* Drag and Drop Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive 
              ? 'border-emerald-500 bg-emerald-50' 
              : 'border-gray-300 hover:border-emerald-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            Drag and drop files here, or{' '}
            <button
              onClick={() => setShowUpload(true)}
              className="text-emerald-600 hover:underline"
            >
              click to upload
            </button>
          </p>
        </div>

        {/* Files Grid */}
        {loading ? (
          <div className="text-center py-8">Loading files...</div>
        ) : files.length === 0 ? (
          <div className="text-center py-8">
            <FileIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No files uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {files.map((file) => (
              <Card key={file.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getFileIcon(file.file_type)}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm truncate" title={file.original_filename}>
                          {file.original_filename}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.file_size)}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  {file.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {file.description}
                    </p>
                  )}
                  
                  {file.folder && (
                    <div className="flex items-center gap-1 mb-2">
                      <Folder className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">{file.folder}</span>
                    </div>
                  )}
                  
                  {file.tags && file.tags.length > 0 && (
                    <div className="flex items-center gap-1 mb-3">
                      <Tag className="w-3 h-3 text-gray-400" />
                      <div className="flex flex-wrap gap-1">
                        {file.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {tag}
                          </span>
                        ))}
                        {file.tags.length > 2 && (
                          <span className="text-xs text-gray-400">
                            +{file.tags.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-400">
                      {new Date(file.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFileDownload(file.id, file.original_filename)}
                        className="h-8 w-8 p-0"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFileEdit(file)}
                        className="h-8 w-8 p-0"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFileDelete(file.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload File</DialogTitle>
              <DialogDescription>
                Add a file to your personal storage library
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="file">File</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files[0] }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  value={uploadData.description}
                  onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this file..."
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="folder">Folder (Optional)</Label>
                <Input
                  id="folder"
                  value={uploadData.folder}
                  onChange={(e) => setUploadData(prev => ({ ...prev, folder: e.target.value }))}
                  placeholder="e.g., Documents, Images, Projects"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="tags">Tags (Optional)</Label>
                <Input
                  id="tags"
                  value={uploadData.tags}
                  onChange={(e) => setUploadData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="e.g., training, report, important (comma separated)"
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowUpload(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleFileUpload}
                  disabled={!uploadData.file || loading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditFile} onOpenChange={setShowEditFile}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit File Details</DialogTitle>
              <DialogDescription>
                Update file information and organization
              </DialogDescription>
            </DialogHeader>
            
            {editingFile && (
              <div className="space-y-4">
                <div>
                  <Label>Filename</Label>
                  <Input
                    value={editingFile.original_filename}
                    disabled
                    className="mt-1 bg-gray-50"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editingFile.description || ""}
                    onChange={(e) => setEditingFile(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe this file..."
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-folder">Folder</Label>
                  <Input
                    id="edit-folder"
                    value={editingFile.folder || ""}
                    onChange={(e) => setEditingFile(prev => ({ ...prev, folder: e.target.value }))}
                    placeholder="e.g., Documents, Images, Projects"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-tags">Tags</Label>
                  <Input
                    id="edit-tags"
                    value={editingFile.tags ? editingFile.tags.join(', ') : ""}
                    onChange={(e) => {
                      const tags = e.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
                      setEditingFile(prev => ({ ...prev, tags }));
                    }}
                    placeholder="e.g., training, report, important (comma separated)"
                    className="mt-1"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => setShowEditFile(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleFileUpdate}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    Update
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}